import {SchoolIcon} from "../../components/icons/school";
import {AddButton} from "@ciq/shared/components/buttons";
import Search from "@ciq/shared/components/search";
import {forwardRef, useEffect, useReducer, useRef, useState} from "react";
import {EntryModal, EntryInput, EntrySelector} from "@ciq/shared/components/modal";
import {ListView} from "@ciq/shared/components/list";
import {EmptyView} from "@ciq/shared/components/views";

const TIMEZONE_OPTIONS = [
  { label: "US/Eastern", value: "US/Eastern" },
  { label: "US/Central", value: "US/Central" },
  { label: "US/Mountain", value: "US/Mountain" },
  { label: "US/Pacific", value: "US/Pacific" },
  { label: "US/Alaska", value: "US/Alaska" },
  { label: "US/Hawaii", value: "US/Hawaii" },
  { label: "UTC", value: "UTC" },
]

const DEFAULT_FORM_DATA = {
  name: "",
  description: "",
  address: "",
  city: "",
  state: "",
  country: "USA",
  zip: "",
  latitude: "",
  longitude: "",
  zoom: "14",
  timezone: null,
}

const DEFAULT_STATE = {
  search: {
    isSearching: false,
    value: null,
    lowered: null
  },
  newModal: {
    show: false
  }
}

const SET_SEARCH = "SET_SEARCH"
const SHOW_NEW_MODAL = "SHOW_NEW_MODAL"

const SchoolListViewReducer = (state = DEFAULT_STATE, action) => {
  switch (action.type) {
    case SET_SEARCH:
      return {
        ...state,
        search: {
          isSearching: action.payload != null,
          value: action.payload,
          lowered: action.payload != null ? action.payload.toLowerCase() : null,
        }
      }

    case SHOW_NEW_MODAL:
      return {
        ...state,
        newModal: {
          show: action.payload
        }
      }

    default:
      throw new Error(`Unknown action type ${action.type}`)
  }
}

export function SchoolListView({school, schools, onSchoolAdded, onSchoolSelected}) {
  const [state, dispatch] = useReducer(SchoolListViewReducer, DEFAULT_STATE)
  const [listViewHeight, setListViewHeight] = useState("0")

  const viewRef = useRef(null)
  const headerRef = useRef(null)

  const viewBaseHeight = viewRef.current != null ? Math.floor(viewRef.current.getBoundingClientRect().height) : null
  const headerBaseHeight = headerRef.current != null ? Math.floor(headerRef.current.getBoundingClientRect().height) : null

  useEffect(() => {
    if (viewBaseHeight == null || headerBaseHeight == null) {
      return
    }

    const parentHeight = `calc(${viewBaseHeight}px - var(--content-inset) * 2)`
    const headerHeight = `calc(${headerBaseHeight}px + var(--content-inset))`

    const height = `calc(${parentHeight} - ${headerHeight})`
    if (height !== listViewHeight) {
      setListViewHeight(height)
    }
  }, [viewBaseHeight, headerBaseHeight, listViewHeight]);

  const setSearch = search => dispatch({type: SET_SEARCH, payload: search})

  const showNewModal = () => dispatch({type: SHOW_NEW_MODAL, payload: true})
  const hideNewModal = () => dispatch({type: SHOW_NEW_MODAL, payload: false})

  const handleOnSchoolAdded = (school) => {
    onSchoolAdded && onSchoolAdded(school, hideNewModal)
  }

  const filterSchool = (school) => {
    if (state.search.lowered == null) return true
    return school.id === state.search.lowered || school.name.toLowerCase().includes(state.search.lowered)
  }

  const filteredSchools = (schools || []).filter(filterSchool)
  const hasSchools = filteredSchools.length > 0

  return (
    <div ref={viewRef} className="school-list-view da-card">
      <HeaderView ref={headerRef} search={state.search.value} onSearch={setSearch} onNewSchoolClick={showNewModal}/>
      <ListView>
        {!hasSchools && <EmptyView>No Institutions</EmptyView>}
        {hasSchools && filteredSchools.map(s =>
          <ListViewItem
            key={s.id}
            school={s}
            isSelected={school.id === s.id}
            onClick={onSchoolSelected}
          />
        )}
      </ListView>
      <NewSchoolModal
        show={state.newModal.show}
        onHide={hideNewModal}
        onNewSchoolClick={handleOnSchoolAdded}
      />
    </div>
  )

}

const HeaderView = forwardRef(({search, onSearch, onNewSchoolClick}, ref) => {
  const handleOnNewSchoolClick = () => {
    onNewSchoolClick && onNewSchoolClick();
  }

  return (
    <div ref={ref} className="school-list-view-header">
      <AddButton onClick={handleOnNewSchoolClick}>New Institution</AddButton>
      <Search search={search} onSearch={onSearch}/>
    </div>
  )
})

function ListViewItem({school, isSelected, onClick}) {
  const handleOnClick = () => {
    onClick && onClick(school)
  }

  return (
    <div className="school-list-view-item" aria-selected={isSelected} onClick={handleOnClick}>
      <SchoolIcon school={school}/>
      <span>{school.name}</span>
    </div>
  )
}

// Export reducer and constants for testing
export {
  SchoolListViewReducer,
  DEFAULT_STATE,
  DEFAULT_FORM_DATA,
  TIMEZONE_OPTIONS,
  SET_SEARCH,
  SHOW_NEW_MODAL,
}

function NewSchoolModal({show, onHide, onNewSchoolClick}) {
  const [data, setData] = useState(DEFAULT_FORM_DATA)

  useEffect(() => {
    if (!show) {
      setData(DEFAULT_FORM_DATA)
    }
  }, [show])

  if (!show) return null

  const updateField = (field) => (value) => {
    setData(prev => ({ ...prev, [field]: value }))
  }

  const isButtonDisabled = () => {
    // Required fields validation
    const requiredFields = ["name", "address", "city", "state", "country", "zip", "latitude", "longitude", "zoom"]
    const hasAllRequired = requiredFields.every(field => data[field]?.toString().trim())
    const hasTimezone = data.timezone != null

    return !hasAllRequired || !hasTimezone
  }

  const handleOnClick = () => {
    // Format data for API
    const payload = {
      name: data.name.trim(),
      description: data.description?.trim() || null,
      address: data.address.trim(),
      city: data.city.trim(),
      state: data.state.trim(),
      country: data.country.trim(),
      zip: data.zip.trim(),
      latitude: parseFloat(data.latitude),
      longitude: parseFloat(data.longitude),
      zoom: parseInt(data.zoom, 10),
      timezone: data.timezone?.value,
      // API requires gpa_system but we'll use defaults since ticket says remove from UI
      gpa_system: {
        minimum: 0.0,
        maximum: 4.0,
        step: 1,
      },
    }
    onNewSchoolClick && onNewSchoolClick(payload)
  }

  const buttons = {
    secondary: {
      title: "Cancel",
      onClick: onHide
    },
    primary: {
      title: "Create",
      disabled: isButtonDisabled(),
      onClick: handleOnClick
    }
  }

  return (
    <EntryModal show={show} title="Create Institution" buttons={buttons} onHide={onHide}>
      <EntryInput
        label="Name"
        value={data.name}
        onChange={updateField("name")}
        autoFocus
      />
      <EntryInput
        label="Description"
        value={data.description}
        onChange={updateField("description")}
      />
      <EntryInput
        label="Address"
        value={data.address}
        onChange={updateField("address")}
      />
      <EntryInput
        label="City"
        value={data.city}
        onChange={updateField("city")}
      />
      <EntryInput
        label="State"
        value={data.state}
        onChange={updateField("state")}
      />
      <EntryInput
        label="Country"
        value={data.country}
        onChange={updateField("country")}
      />
      <EntryInput
        label="Zip"
        value={data.zip}
        onChange={updateField("zip")}
      />
      <EntryInput
        label="Latitude"
        value={data.latitude}
        onChange={updateField("latitude")}
        type="number"
      />
      <EntryInput
        label="Longitude"
        value={data.longitude}
        onChange={updateField("longitude")}
        type="number"
      />
      <EntryInput
        label="Zoom"
        value={data.zoom}
        onChange={updateField("zoom")}
        type="number"
      />
      <EntrySelector
        label="Timezone"
        value={data.timezone}
        options={TIMEZONE_OPTIONS}
        onChange={updateField("timezone")}
      />
    </EntryModal>
  )
}